import { COLUMN_WIDTHS } from '@bridge/constants';
import { formatDate, formatDateString } from '@bridge/utils';
import { createColumnHelper } from '@tanstack/react-table';

import { DateDisplay } from '../../components/DateDisplay';
import { EmptyValue } from '../../components/EmptyValue';
import { Body } from '../../typography';
import TableColumnHeader from '../TableColumnHeader';

import type { CellContext } from '@tanstack/react-table';
import type { ColumnFactory, DateColumnOptions } from './types';

/**
 * Factory function to create a date column with formatting and optional sorting
 *
 * @example
 * ```tsx
 * const CREATED_DATE_COLUMN = createDateColumn<Customer>({
 *   accessor: 'formattedCreatedAt',
 *   label: 'Date added',
 *   options: { sortable: true }
 * });
 * ```
 */
export function createDateColumn<TData>(config: {
  accessor: keyof TData & string;
  label: string;
  options?: DateColumnOptions<TData>;
}): ColumnFactory<TData> {
  const { accessor, label, options = {} } = config;
  const {
    size = COLUMN_WIDTHS.dateShort,
    sortable = false,
    className,
    infoTooltip,
    formatter,
    formatOptions = { month: '2-digit', day: '2-digit', year: '2-digit' },
    useTooltip = false,
    dateDisplayProps,
  } = options;

  const columnHelper = createColumnHelper<TData>();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return columnHelper.accessor(accessor as any, {
    size,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    header: ({ table }: { table: any }) => (
      <TableColumnHeader
        accessor={accessor}
        infoTooltip={infoTooltip}
        label={label}
        {...(sortable ? { setSorting: table.setSorting, sortable: true as const } : {})}
        tableState={table.getState()}
      />
    ),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    cell: (info: CellContext<TData, any>) => {
      const value = info.getValue();

      if (value == null) {
        return <EmptyValue className={className} size="md" />;
      }

      if (useTooltip) {
        return <DateDisplay date={value as string | Date} {...dateDisplayProps} />;
      }

      let displayValue: string;
      if (formatter) {
        displayValue = formatter(value as Date | string, info.row.original);
      } else if (typeof value === 'string') {
        // Already formatted string (common in API responses)
        displayValue = formatDateString(value, formatOptions);
      } else if (value instanceof Date) {
        displayValue = formatDate(value, formatOptions);
      } else {
        displayValue = String(value);
      }

      return <Body className={className}>{displayValue}</Body>;
    },
  });
}
